// Receipt.jsx
import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { API_BASE } from "../api/config";
import { useAuth } from "../context/AuthContext"; // ✅ get branch & username

function Receipt() {
  const { transaction_number } = useParams();
  const { username, branch } = useAuth();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [footerText, setFooterText] = useState("");
  const [branchDetails, setBranchDetails] = useState(null);
  const receiptRef = useRef();

  // ✅ Format for footer datetime
  const formatDateTime = (date) => {
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    const hh = String(date.getHours()).padStart(2, "0");
    const mi = String(date.getMinutes()).padStart(2, "0");
    const ss = String(date.getSeconds()).padStart(2, "0");
    return `${dd}-${mm}-${yyyy} ${hh}:${mi}:${ss}`;
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // ✅ Fetch Payment Details
  useEffect(() => {
    const fetchPayment = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${API_BASE}?key=dzital_key&pswd=Hibujji@123&transac=get&table=payments&condition=get_payments_by_transaction_number&transaction_number=${transaction_number}`
        );
        const data = await res.json();
        if (data && data.length > 0) {
          setPayment(data[0]);
        } else {
          setError("Payment not found.");
        }
      } catch (err) {
        console.error(err);
        setError("Error fetching payment.");
      } finally {
        setLoading(false);
      }
    };
    fetchPayment();
  }, [transaction_number]);

  // ✅ Fetch Company Footer Data
  useEffect(() => {
    fetch(`${API_BASE}?key=dzital_key&pswd=Hibujji@123&transac=get&table=company`)
      .then((res) => res.json())
      .then((data) => {
        if (data?.status === "ok" && data?.data?.length > 0) {
          setFooterText(data.data[0].terms_and_conditions || "");
        }
      })
      .catch((err) => console.error("❌ Error fetching company footer:", err));
  }, []);

  // ✅ Fetch Branch Details
  useEffect(() => {
    fetch(
      `${API_BASE}?key=dzital_key&pswd=Hibujji@123&transac=get&table=branches&condition=get_branch_det&branch=${branch}`
    )
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setBranchDetails(data[0]);
        }
      })
      .catch((err) => console.error("Error fetching branch details:", err));
  }, [branch]);

  const handlePrint = () => {
    if (!receiptRef.current) return;

    const mediaQueryList = window.matchMedia("print");
    const afterPrintHandler = () => {
      setTimeout(() => window.close(), 1000);
    };

    mediaQueryList.addEventListener("change", (e) => {
      if (!e.matches) {
        afterPrintHandler();
      }
    });

    window.print();

    // Fallback
    setTimeout(() => {
      window.close();
    }, 5000);
  };

  if (loading) {
    return <div className="p-6 text-center">Loading receipt...</div>;
  }

  if (error) {
    return <div className="p-6 text-center text-red-600">{error}</div>;
  }

  const voucherType =
    payment.payment_mode_type === "debit"
      ? "PAYMENT VOUCHER"
      : "RECEIPT VOUCHER";

  return (
    <div className="receipt p-2 relative" ref={receiptRef}>
      {/* ✅ Cancelled Watermark */}
      {payment.cancel_status === 1 || payment.cancel_status === "1" ? (
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-none select-none"
          style={{
            opacity: 0.15,
            transform: "rotate(-30deg)",
            fontSize: "80px",
            fontWeight: "bold",
            color: "red",
            zIndex: 0,
          }}
        >
          CANCELLED
        </div>
      ) : null}

      <div style={{ position: "relative", zIndex: 1 }}>
        {/* ✅ Branch Header */}
        <h2
          style={{
            textAlign: "center",
            fontSize: "18px",
            margin: "5px 0",
          }}
        >
          {branchDetails?.name || "Restaurant"}
        </h2>
        <p
          style={{
            textAlign: "center",
            fontSize: "16px",
            marginBottom: "8px",
          }}
        >
          {branchDetails?.address || ""}
        </p>

        {/* ✅ Voucher Type */}
        <h3
          style={{
            textAlign: "center",
            fontWeight: "bold",
            marginBottom: "5px",
          }}
        >
          {voucherType}
        </h3>

        <div className="border-b mb-2">
          <div>
            <strong>Transaction:</strong> {payment.transaction_number}
          </div>
          <div>
            <strong>Date:</strong>{" "}
            {formatDate(payment.invoice_date)} {payment.invoice_time}
          </div>
          <div>
            <strong>Customer:</strong> {payment.customer_name} (
            {payment.billing_adress},{payment.customer_mobile})
          </div>
        </div>

        <div className="border-b mb-2">
          <div>
            <strong>Payment Mode:</strong> {payment.payment_mode}
          </div>
          {payment.cash && (
            <div>
              <strong>Cash:</strong> ₹{payment.cash}
            </div>
          )}
          {payment.online_cash && (
            <div>
              <strong>Online:</strong> ₹{payment.online_cash}
            </div>
          )}
          <div>
            <strong>Paid Amount:</strong> ₹{payment.paid_amount}
          </div>
          {payment.remark && (
            <div>
              <strong>Remark:</strong>{" "}
              {payment.cancel_status === 1 || payment.cancel_status === "1"
                ? `Cancelled: ${payment.remark}`
                : payment.remark}
            </div>
          )}
        </div>

        {/* ✅ Footer */}
        {footerText && (
          <div
            style={{
              textAlign: "center",
              marginTop: 16,
              fontSize: 12,
              color: "#555",
            }}
          >
            {footerText}
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: 12 }}>
          <p style={{ fontSize: 11, color: "#777", marginTop: 4 }}>
            Generated on {formatDateTime(new Date())} by {username} ({branch})
          </p>
        </div>

        {/* ✅ Hide Print Button for cancelled receipts */}
        {payment.cancel_status !== 1 && payment.cancel_status !== "1" && (
          <div className="mt-2">
            <button
              onClick={handlePrint}
              className="w-full bg-blue-600 text-white py-1 rounded-lg hover:bg-blue-700 text-sm"
            >
              PRINT {voucherType}
            </button>
          </div>
        )}
      </div>

      {/* ✅ Print styles for thermal printer */}
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
              margin: 0;
              padding: 0;
            }
            .receipt, .receipt * {
              visibility: visible;
            }
            .receipt {
              position: absolute;
              left: 0;
              top: 0;
              width: 80mm;
              font-family: monospace;
              font-size: 12px;
              padding: 2mm;
              color: #000;
            }
            button {
              display: none;
            }
            .border-b {
              border-bottom: 1px dashed #000;
              margin-bottom: 2mm;
              padding-bottom: 1mm;
            }
            .cancelled-watermark {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%) rotate(-30deg);
              font-size: 80px;
              font-weight: bold;
              color: red;
              opacity: 0.15;
              text-align: center;
              z-index: 0;
            }
          }
        `}
      </style>
    </div>
  );
}

export default Receipt;
